import React from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth, hasRole } from "../../contexts/AuthContext";
import { useI18n } from "../../contexts/I18nContext";
import {
    LayoutDashboard, FolderKanban, Newspaper, Sparkles, Building2,
    Users, FileText, Mail, LogOut, Globe, Hash, MessageSquare, Globe2,
    Settings, Heart, Image, Contact
} from "lucide-react";
import LegendsMark from "../../components/LegendsMark";

const ITEMS = [
    { to: "/admin", key: "admin_dashboard", icon: LayoutDashboard, end: true, roles: ["super_admin", "editor", "news_editor"] },
    { to: "/admin/settings", key: "admin_site_settings", icon: Settings, roles: ["editor"] },
    { to: "/admin/works", key: "admin_works", icon: FolderKanban, roles: ["editor"] },
    { to: "/admin/news", key: "admin_news", icon: Newspaper, roles: ["news_editor"] },
    { to: "/admin/services", key: "admin_services", icon: Sparkles, roles: ["editor"] },
    { to: "/admin/values", key: "admin_values", icon: Heart, roles: ["editor"] },
    { to: "/admin/stats", key: "admin_stats", icon: Hash, roles: ["editor"] },
    { to: "/admin/media", key: "admin_media", icon: Image, roles: ["editor"] },
    { to: "/admin/brand/media", key: "admin_global", icon: Globe2, roles: ["editor"] },
    { to: "/admin/clients", key: "admin_clients", icon: Building2, roles: ["editor"] },
    { to: "/admin/business-cards", key: "admin_business_cards", icon: Contact, roles: ["editor"] },
    { to: "/admin/welcome", key: "admin_welcome", icon: MessageSquare, roles: ["editor"] },
    { to: "/admin/profile", key: "admin_profile", icon: FileText, roles: ["editor"] },
    { to: "/admin/users", key: "admin_users", icon: Users, roles: ["super_admin"] },
    { to: "/admin/contacts", key: "admin_contacts", icon: Mail, roles: ["super_admin"] },
];

export default function AdminLayout() {
    const { user, loading, logout } = useAuth();
    const { t, toggle, lang } = useI18n();
    const navigate = useNavigate();

    if (loading) return <div className="p-10 text-sm text-[#6B7280]">…</div>;
    if (!user || user === false) return <Navigate to="/admin/login" replace />;

    const allowed = ITEMS.filter((i) => hasRole(user, ...i.roles));

    return (
        <div className="min-h-screen flex bg-[#EFF0F1]" data-testid="admin-layout">
            <aside className="w-64 bg-[#002951] text-white flex flex-col" data-testid="admin-sidebar">
                <div className="p-6 border-b border-white/10">
                    <div className="heading text-xl tracking-tighter inline-flex items-center gap-1">LEGENDS<LegendsMark size={18} color="#0060A7" /></div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-1">Admin</div>
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {allowed.map((it) => (
                        <NavLink
                            key={it.to} to={it.to} end={it.end}
                            data-testid={`admin-nav-${it.key}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                                    isActive ? "bg-white text-[#002951]" : "text-white/80 hover:bg-white/10"
                                }`
                            }
                        >
                            <it.icon size={16} />
                            {t(it.key)}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-white/10 space-y-1">
                    <button
                        onClick={toggle}
                        data-testid="admin-language-toggle"
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-white/80 hover:bg-white/10 transition-colors"
                    >
                        <Globe size={16} />
                        {lang === "ar" ? "English" : "العربية"}
                    </button>
                    <button
                        onClick={async () => { await logout(); navigate("/admin/login"); }}
                        data-testid="admin-logout-button"
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-white/80 hover:bg-white/10 transition-colors"
                    >
                        <LogOut size={16} />
                        {t("admin_logout")}
                    </button>
                </div>
            </aside>
            <main className="flex-1 overflow-y-auto" data-testid="admin-main">
                <div className="border-b border-black/10 bg-white px-8 py-5 flex items-center justify-between">
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-[#6B7280]">{user.role.replace("_", " ")}</div>
                        <div className="heading text-2xl tracking-tight" data-testid="admin-user-name">{user.name}</div>
                    </div>
                    <div className="text-xs text-[#6B7280]" data-testid="admin-user-email">{user.email}</div>
                </div>
                <div className="p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
